// contextPlugin.ts
// Context management plugin for managing conversation context

export interface ContextEntry {
  role: string;
  content: string;
  timestamp: number;
}

export class ContextPlugin {
  private maxContextLength: number;
  private contextWindow: ContextEntry[];

  constructor(maxLength: number = 10) {
    this.maxContextLength = maxLength;
    this.contextWindow = [];
  }

  addEntry(role: string, content: string): void {
    const entry: ContextEntry = {
      role,
      content,
      timestamp: Date.now()
    };

    this.contextWindow.push(entry);

    // Maintain context window size
    if (this.contextWindow.length > this.maxContextLength) {
      // Keep system message if it exists
      const systemMessage = this.contextWindow.find(e => e.role === 'system');
      if (systemMessage && this.contextWindow[0] === systemMessage) {
        this.contextWindow = [systemMessage, ...this.contextWindow.slice(2)];
      } else {
        this.contextWindow.shift();
      }
    }
  }

  getContext(): ContextEntry[] {
    return [...this.contextWindow];
  }

  clearContext(): void {
    // Keep system message if it exists
    const systemMessage = this.contextWindow.find(e => e.role === 'system');
    this.contextWindow = systemMessage ? [systemMessage] : [];
  }

  setSystemMessage(content: string): void {
    // Remove existing system message
    this.contextWindow = this.contextWindow.filter(e => e.role !== 'system');
    
    // Add new system message at the beginning
    this.contextWindow.unshift({
      role: 'system',
      content,
      timestamp: Date.now()
    });
  }

  getFormattedContext(): Array<{ role: 'user' | 'assistant' | 'system'; content: string }> {
    return this.contextWindow.map(entry => ({
      role: entry.role as 'user' | 'assistant' | 'system',
      content: entry.content
    }));
  }

  getTotalTokenEstimate(): number {
    // Rough estimate: 1 token ≈ 4 characters
    const totalChars = this.contextWindow.reduce((sum, entry) => 
      sum + entry.content.length, 0
    );
    return Math.ceil(totalChars / 4);
  }

  optimizeContext(maxTokens: number = 4000): void {
    while (this.getTotalTokenEstimate() > maxTokens && this.contextWindow.length > 2) {
      // Keep system message and most recent exchange
      const systemMessage = this.contextWindow.find(e => e.role === 'system');
      if (systemMessage && this.contextWindow[0] === systemMessage) {
        this.contextWindow.splice(1, 1); // Remove second oldest
      } else {
        this.contextWindow.shift(); // Remove oldest
      }
    }
  }
}